import {Helmet} from "react-helmet";
import {Link} from "wouter";
import {Button} from "../components/ui/button";

const Partner = () => (
    <>
        <Helmet>
            <title>Partner With Us | SurpriseTable</title>
            <meta
                name="description"
                content="Grow your restaurant’s reach, attract new guests, and fill empty tables by partnering with SurpriseTable. Join the movement of curated surprise dining experiences."
            />
        </Helmet>

        <section className="bg-white py-16 transition-colors duration-200 dark:bg-gray-900">
            <div className="container mx-auto px-4">
                {/* HERO */}
                <div className="mb-14 text-center max-w-3xl mx-auto">
                    <h1 className="mb-4 font-serif text-4xl md:text-5xl font-bold text-gray-800 dark:text-white">
                        Become a <span className="text-primary">SurpriseTable</span> Partner
                    </h1>
                    <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                        Join Europe’s most exciting dining movement. Attract new guests, fill empty tables, and showcase
                        your cuisine—risk-free. <b>Zero monthly fees. No commitment. Only results.</b>
                    </p>
                    <Button
                        asChild
                        className="mt-8 px-10 py-4 text-lg font-bold bg-primary text-white rounded-lg hover:bg-primary/90"
                        size="lg"
                    >
                        <Link href="/contact">Become a Partner Today</Link>
                    </Button>
                </div>

                <div className="mx-auto max-w-5xl">
                    {/* Step 1 */}
                    <div className="mb-16 md:flex md:items-center">
                        <div className="mb-8 md:mb-0 md:w-1/2 md:pr-12">
                            <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                <span
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                  1
                </span>
                            </div>
                            <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                                Fill Your Empty Tables – Effortlessly
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We connect your restaurant with motivated new diners who book and pay in advance. You
                                only accept surprise guests on days/times you choose—no hassle, no risk.
                            </p>
                        </div>
                        <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                            <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                                What you provide:
                            </h3>
                            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Open tables (only when you want them filled)
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Chef's surprise menu (valued over €15)
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    A welcoming experience—our guests are ready to discover!
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="mb-16 md:flex md:flex-row-reverse md:items-center">
                        <div className="mb-8 md:mb-0 md:w-1/2 md:pl-12">
                            <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                <span
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                  2
                </span>
                            </div>
                            <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                                Attract New Customers—Build Lasting Relationships
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                Every SurpriseTable guest is a potential new regular. 70%+ of our guests revisit partner
                                restaurants. Your venue gets promoted through our platform, marketing campaigns, and
                                social channels—at zero cost.
                            </p>
                        </div>
                        <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                            <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                                What you receive:
                            </h3>
                            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Booked & paid guests—no-shows are rare
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Exposure to a food-loving, experience-driven audience
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Dedicated partner success team to help you shine
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="mb-16 md:flex md:items-center">
                        <div className="mb-8 md:mb-0 md:w-1/2 md:pr-12">
                            <div className="rounded-full bg-primary/10 p-3 inline-block mb-4">
                <span
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                  3
                </span>
                            </div>
                            <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                                Get Paid—Fast & Fair
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We pay you directly after every booking—no waiting, no hidden fees. You set your own
                                rules. We bring the diners.
                            </p>
                        </div>
                        <div className="rounded-xl bg-gray-100 p-6 shadow-lg dark:bg-gray-800 md:w-1/2">
                            <h3 className="mb-3 font-medium text-gray-800 dark:text-white">
                                Partner benefits:
                            </h3>
                            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Instant payout—no invoices, no stress
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Analytics & dashboard to track your bookings
                                </li>
                                <li className="flex items-center">
                                    <svg className="mr-2 h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4"/>
                                    </svg>
                                    Cancel any time—no long-term contract
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Testimonials */}
                    <div className="mb-20 mt-16 mx-auto max-w-3xl text-center">
                        <h3 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
                            What Our Partners Say
                        </h3>
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="rounded-lg bg-gray-100 p-6 dark:bg-gray-800 shadow">
                                <p className="italic text-gray-700 dark:text-gray-300">
                                    “SurpriseTable guests are fantastic! They’re open-minded, love our food, and several
                                    became regulars. Our off-peak hours are booked weeks in advance.”
                                </p>
                                <span className="mt-4 block font-bold text-primary">Giuseppe R., Osteria Milano</span>
                            </div>
                            <div className="rounded-lg bg-gray-100 p-6 dark:bg-gray-800 shadow">
                                <p className="italic text-gray-700 dark:text-gray-300">
                                    “No more last-minute empty tables. Their team is responsive, the tech is easy, and
                                    we get paid every week. Highly recommend!”
                                </p>
                                <span
                                    className="mt-4 block font-bold text-primary">Sabine L., Café Zeitlos Berlin</span>
                            </div>
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="mt-20 text-center">
                        <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
                            Ready to Grow Your Restaurant With Us?
                        </h2>
                        <p className="mb-4 text-lg text-gray-700 dark:text-gray-300">
                            It takes less than 5 minutes to join. Start getting bookings, not just promises.
                        </p>
                        <Button
                            asChild
                            className="inline-block rounded-lg bg-primary px-10 py-4 text-lg font-bold text-white hover:bg-red-700"
                            size="lg"
                        >
                            <Link href="/contact">Partner With SurpriseTable</Link>
                        </Button>
                        <div className="mt-4 text-xs text-gray-400">Deviofy FZCO, DSO-THUB, Dubai Digital Park, Building A2, Dubai Silicon Oasis, Dubai, UAE.· Trusted by restaurants across

                        </div>
                    </div>
                </div>
            </div>
        </section>
    </>
);

export default Partner;
